/**
 * Tela 20 — Administração › Cadastros base financeiros (doc 09: "parâmetros
 * financeiros versionados, plano de contas, centro de custo"; conta bancária).
 * CRUD simples com inativação no lugar de exclusão (DEC-10).
 * Financeiro A1 (0038): estrutura gerencial do plano de contas, conta principal e saldo inicial, cartões da
 * empresa (só o final, sem dado sensível) e pessoas financeiras (identidade própria; vínculo com usuário opcional).
 * Cadastrar não cria lançamento, obrigação nem movimento.
 */
import { usePersonalizacao } from '../comercial/personalizacao.tsx';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, consulta, ErroApi } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { Aviso, Campo, ConfirmarAcao, Modal, Selo, SeloStatus, Vazio } from '../componentes/basicos.tsx';
import { data } from '../formatos.ts';
import { Ajuda } from '../comercial/ui.tsx';

type Registro = Record<string, any> & { id: string; status?: string; atualizado_em: string };

interface DefCampo {
  nome: string;
  rotulo: string;
  tipo?: 'texto' | 'select' | 'conta_pai' | 'simnao' | 'numero' | 'data' | 'ref' | 'area';
  opcoes?: { valor: string; rotulo: string }[];
  obrigatorio?: boolean;
  max?: number;
  sugestoes?: string[];
  ajuda?: string;
  /** tipo 'ref': lista de outro cadastro (carregada só com permissão de visualizar). */
  ref?: { chave: string; rota: string; entidade: string; rotulo: (r: Registro) => string; vazio: string };
  /** valor exibido no formulário a partir do registro (ex.: dinheiro no formato brasileiro). */
  inicial?: (r: Registro) => string;
}

interface DefCadastro {
  entidade: string;
  rota: string;
  singular: string;
  /** rótulo do botão e do título de criação (ex.: "Novo cartão", "Nova pessoa") */
  novo: string;
  colunas: { rotulo: string; valor: (r: Registro) => ReactNode }[];
  campos: DefCampo[];
  /** explicação secundária da lista, no "?" ao lado do botão de criação */
  nota?: string;
}

export const NATUREZAS: Record<string, string> = {
  receita_operacional: 'Receita operacional',
  deducao: 'Dedução da receita',
  despesa_operacional: 'Despesa operacional',
  despesa_sem_desembolso: 'Despesa sem desembolso',
  receita_financeira: 'Receita financeira',
  despesa_financeira: 'Despesa financeira',
  receita_nao_operacional: 'Receita não operacional',
  despesa_nao_operacional: 'Despesa não operacional',
  patrimonial: 'Patrimonial',
  transferencia: 'Transferência entre contas',
  ajuste: 'Ajuste',
  ativo: 'Ativo',
  adiantamento: 'Adiantamento',
  passivo: 'Passivo',
};
const TIPOS_CONTA: Record<string, string> = { receita: 'Receita', despesa: 'Despesa', movimento: 'Movimento' };
const TIPOS_PESSOA: Record<string, string> = { pf: 'Pessoa física', estrangeiro: 'Estrangeiro', orgao_sem_cnpj: 'Órgão sem CNPJ' };
const simNao = (v: unknown) => (v === true ? 'sim' : v === false ? 'não' : '—');
const reais = (v: unknown) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const linhas = (a: ReactNode, b?: ReactNode) => (
  <>
    <span className="duas-linhas">{a}</span>
    {b ? <div className="texto-secundario">{b}</div> : null}
  </>
);
const SIM_NAO = [
  { valor: 'true', rotulo: 'Sim' },
  { valor: 'false', rotulo: 'Não' },
];

export const CADASTROS: Record<string, DefCadastro> = {
  'plano-contas': {
    entidade: 'plano_contas',
    rota: '/api/plano-contas',
    singular: 'conta',
    novo: 'Nova conta',
    nota: '“Pode ser usada em obrigação” indica só a possibilidade de uso no Contas a Pagar — cadastrar uma conta não cria lançamentos.',
    colunas: [
      { rotulo: 'Código', valor: (r) => <span className="nowrap">{r.codigo}</span> },
      { rotulo: 'Conta', valor: (r) => linhas(r.nome, r.conta_pai_codigo ? `Conta pai: ${r.conta_pai_codigo} — ${r.conta_pai_nome}` : null) },
      { rotulo: 'Grupo da DRE · Categoria', valor: (r) => (r.grupo_dre ? linhas(r.grupo_dre, r.categoria) : '—') },
      { rotulo: 'Tipo · Natureza', valor: (r) => linhas(<Selo tom="destaque">{TIPOS_CONTA[r.tipo] ?? r.tipo}</Selo>, r.natureza ? NATUREZAS[r.natureza] : null) },
      { rotulo: 'Resultado · Caixa', valor: (r) => (r.impacta_dre === null && r.impacta_caixa === null ? '—' : `DRE ${simNao(r.impacta_dre)} · caixa ${simNao(r.impacta_caixa)}`) },
      { rotulo: 'Centro padrão', valor: (r) => (r.centro_custo_padrao_codigo ? linhas(r.centro_custo_padrao_nome, r.centro_custo_padrao_codigo) : '—') },
      { rotulo: 'Uso em obrigação', valor: (r) => (r.pode_gerar_obrigacao ? 'Pode' : 'Não') },
    ],
    campos: [
      { nome: 'codigo', rotulo: 'Código', obrigatorio: true, max: 30 },
      { nome: 'nome', rotulo: 'Nome', obrigatorio: true, max: 200 },
      { nome: 'tipo', rotulo: 'Tipo', tipo: 'select', obrigatorio: true, opcoes: Object.entries(TIPOS_CONTA).map(([valor, rotulo]) => ({ valor, rotulo })) },
      { nome: 'conta_pai_id', rotulo: 'Conta pai (opcional)', tipo: 'conta_pai' },
      { nome: 'grupo_dre', rotulo: 'Grupo da DRE', max: 100 },
      { nome: 'categoria', rotulo: 'Categoria', max: 100 },
      { nome: 'natureza', rotulo: 'Natureza', tipo: 'select', opcoes: Object.entries(NATUREZAS).map(([valor, rotulo]) => ({ valor, rotulo })), ajuda: 'Nem toda saída é despesa: ativo, adiantamento, transferência e ajuste não entram no resultado.' },
      {
        nome: 'tipo_movimento',
        rotulo: 'Movimento',
        tipo: 'select',
        opcoes: [
          { valor: 'entrada', rotulo: 'Entrada' },
          { valor: 'saida', rotulo: 'Saída' },
          { valor: 'transferencia', rotulo: 'Transferência' },
          { valor: 'ajuste', rotulo: 'Ajuste' },
        ],
      },
      { nome: 'impacta_dre', rotulo: 'Impacta o resultado (DRE)', tipo: 'simnao' },
      { nome: 'impacta_caixa', rotulo: 'Impacta o caixa', tipo: 'simnao' },
      { nome: 'centro_custo_padrao_id', rotulo: 'Centro de custo padrão', tipo: 'ref', ref: { chave: 'centros', rota: '/api/centros-custo?status=ativo', entidade: 'centro_custo', rotulo: (r) => `${r.codigo} — ${r.nome}`, vazio: '(sem centro padrão)' } },
      { nome: 'ordem_dre', rotulo: 'Ordem na DRE', tipo: 'numero' },
      { nome: 'conta_contabil_oficial', rotulo: 'Conta contábil oficial', max: 60, ajuda: 'Fica vazia até a contabilidade definir.' },
      { nome: 'pode_gerar_obrigacao', rotulo: 'Pode ser usada em obrigação', tipo: 'simnao', ajuda: 'Só a possibilidade de uso no Contas a Pagar. Despesa sem desembolso (ex.: depreciação) nunca gera obrigação.' },
      { nome: 'somente_guia_recebida', rotulo: 'Somente guia recebida', tipo: 'simnao', ajuda: 'Conta de guias (DAS e demais): só entra o lançamento da guia efetivamente recebida — sem recorrência nem estimativa.' },
    ],
  },
  'centros-custo': {
    entidade: 'centro_custo',
    rota: '/api/centros-custo',
    singular: 'centro de custo',
    novo: 'Novo centro de custo',
    colunas: [
      { rotulo: 'Código', valor: (r) => r.codigo },
      { rotulo: 'Nome', valor: (r) => r.nome },
    ],
    campos: [
      { nome: 'codigo', rotulo: 'Código', obrigatorio: true, max: 30 },
      { nome: 'nome', rotulo: 'Nome', obrigatorio: true, max: 200 },
    ],
  },
  'contas-bancarias': {
    entidade: 'conta_bancaria',
    rota: '/api/contas-bancarias',
    singular: 'conta bancária',
    novo: 'Nova conta bancária',
    colunas: [
      { rotulo: 'Banco', valor: (r) => linhas(r.banco, r.principal ? <Selo tom="destaque">Principal</Selo> : null) },
      { rotulo: 'Agência', valor: (r) => <span className="nowrap">{r.agencia}</span> },
      { rotulo: 'Número', valor: (r) => <span className="nowrap">{r.numero}</span> },
      { rotulo: 'Tipo', valor: (r) => r.tipo },
      { rotulo: 'Saldo inicial', valor: (r) => (r.saldo_inicial !== null && r.saldo_inicial !== undefined ? linhas(reais(r.saldo_inicial), `em ${data(r.data_saldo_inicial)}`) : '—') },
    ],
    campos: [
      { nome: 'banco', rotulo: 'Banco', obrigatorio: true, max: 100 },
      { nome: 'agencia', rotulo: 'Agência', obrigatorio: true, max: 20 },
      { nome: 'numero', rotulo: 'Número da conta', obrigatorio: true, max: 30 },
      { nome: 'tipo', rotulo: 'Tipo', obrigatorio: true, max: 50, sugestoes: ['corrente', 'poupança', 'pagamento', 'investimento'] },
      { nome: 'principal', rotulo: 'Conta principal', tipo: 'simnao', ajuda: 'Só uma conta ativa pode ser a principal.' },
      { nome: 'saldo_inicial', rotulo: 'Saldo inicial (R$)', max: 20, inicial: (r) => (r.saldo_inicial === null || r.saldo_inicial === undefined ? '' : Number(r.saldo_inicial).toLocaleString('pt-BR', { minimumFractionDigits: 2 })), ajuda: 'Saldo e data de referência vão juntos.' },
      { nome: 'data_saldo_inicial', rotulo: 'Data de referência do saldo', tipo: 'data' },
    ],
  },
  cartoes: {
    entidade: 'cartao',
    rota: '/api/cartoes',
    singular: 'cartão',
    novo: 'Novo cartão',
    nota: 'Cartões da empresa. Guarda só o final do cartão — nunca o número completo, validade ou código.',
    colunas: [
      { rotulo: 'Cartão', valor: (r) => linhas(r.apelido, <span className="nowrap">{`final ${r.final}`}</span>) },
      { rotulo: 'Emissor', valor: (r) => r.emissor },
      { rotulo: 'Fechamento · Vencimento', valor: (r) => `dia ${r.dia_fechamento} · dia ${r.dia_vencimento}` },
      { rotulo: 'Conta padrão', valor: (r) => linhas(r.conta_padrao_banco, <span className="nowrap">{`${r.conta_padrao_agencia} · ${r.conta_padrao_numero}`}</span>) },
    ],
    campos: [
      { nome: 'apelido', rotulo: 'Apelido', obrigatorio: true, max: 80 },
      { nome: 'emissor', rotulo: 'Emissor', obrigatorio: true, max: 100 },
      { nome: 'final', rotulo: 'Final do cartão (4 dígitos)', obrigatorio: true, max: 4 },
      { nome: 'dia_fechamento', rotulo: 'Dia de fechamento', tipo: 'numero', obrigatorio: true },
      { nome: 'dia_vencimento', rotulo: 'Dia de vencimento', tipo: 'numero', obrigatorio: true },
      { nome: 'conta_bancaria_padrao_id', rotulo: 'Conta bancária padrão', tipo: 'ref', obrigatorio: true, ref: { chave: 'contas', rota: '/api/contas-bancarias?status=ativo', entidade: 'conta_bancaria', rotulo: (r) => `${r.banco} · ${r.agencia} · ${r.numero}`, vazio: 'Selecione…' } },
    ],
  },
  'pessoas-financeiras': {
    entidade: 'pessoa_financeira',
    rota: '/api/pessoas-financeiras',
    singular: 'pessoa',
    novo: 'Nova pessoa',
    nota: 'Pessoas e favorecidos que não são Parceiros com CNPJ (empresas continuam em Parceiros comerciais). Não depende de acesso ao HUB.',
    colunas: [
      { rotulo: 'Nome', valor: (r) => linhas(r.nome, TIPOS_PESSOA[r.tipo]) },
      {
        rotulo: 'Documento',
        valor: (r) => (r.cpf ? <span className="nowrap">CPF {r.cpf}</span> : r.identificacao_fiscal ? linhas(<span className="nowrap">{r.identificacao_fiscal}</span>, r.pais) : <span className="texto-secundario">sem documento{r.pais ? ` · ${r.pais}` : ''}</span>),
      },
      { rotulo: 'Usuário do HUB', valor: (r) => (r.usuario_nome ? linhas(r.usuario_nome, r.usuario_ativo === false ? 'usuário inativo' : null) : <span className="texto-secundario">sem vínculo</span>) },
      { rotulo: 'Contato', valor: (r) => (r.email || r.telefone ? linhas(r.email ?? r.telefone, r.email && r.telefone ? r.telefone : null) : '—') },
    ],
    campos: [
      { nome: 'tipo', rotulo: 'Tipo', tipo: 'select', obrigatorio: true, opcoes: Object.entries(TIPOS_PESSOA).map(([valor, rotulo]) => ({ valor, rotulo })) },
      { nome: 'nome', rotulo: 'Nome', obrigatorio: true, max: 200 },
      { nome: 'cpf', rotulo: 'CPF (pessoa física)', max: 14 },
      { nome: 'pais', rotulo: 'País (estrangeiro)', max: 80 },
      { nome: 'identificacao_fiscal', rotulo: 'Identificação fiscal (estrangeiro ou órgão)', max: 60 },
      { nome: 'sem_documento_justificativa', rotulo: 'Sem documento — justificativa', max: 500, ajuda: 'Só quando não houver CPF ou identificação fiscal.' },
      { nome: 'email', rotulo: 'E-mail', max: 160 },
      { nome: 'telefone', rotulo: 'Telefone', max: 40 },
      { nome: 'usuario_id', rotulo: 'Usuário do HUB (opcional)', tipo: 'ref', ref: { chave: 'usuarios', rota: '/api/usuarios', entidade: 'usuario', rotulo: (r) => `${r.nome} — ${r.email}`, vazio: '(sem vínculo)' }, ajuda: 'Informativo. Inativar o usuário não altera a pessoa nem o histórico.' },
      { nome: 'observacao', rotulo: 'Observação', tipo: 'area', max: 1000 },
    ],
  },
};
export const CHAVES_CADASTRO = Object.keys(CADASTROS);

export function TelaCadastro({ chave }: { chave: string }) {
  const def = CADASTROS[chave];
  const { pode } = useSessao();
  const [itens, setItens] = useState<Registro[]>([]);
  const [contasPai, setContasPai] = useState<Registro[]>([]);
  const [refs, setRefs] = useState<Record<string, Registro[]>>({});
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const pers = usePersonalizacao((['plano-contas', 'centros-custo', 'contas-bancarias', 'cartoes', 'pessoas-financeiras'] as const).find((x) => x === chave) ?? null, { busca: { valor: busca, definir: setBusca }, filtros: { status }, aplicarFiltros: (e: Record<string, unknown>) => setStatus((e.status as string) ?? '') });
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<{ tipo: 'form'; r?: Registro } | { tipo: 'status'; r: Registro } | null>(null);

  const carregar = useCallback(async () => {
    try {
      setItens(await api.get(`${def.rota}${consulta({ busca, status })}`));
      if (def.entidade === 'plano_contas') setContasPai(await api.get(`${def.rota}?status=ativo`));
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [def, busca, status]);

  useEffect(() => {
    setErro(null);
    setSucesso(null);
    carregar();
  }, [carregar]);

  // listas de referência dos formulários: só com permissão de visualizar (sem ela, o campo fica com o valor atual)
  useEffect(() => {
    let vivo = true;
    (async () => {
      const r: Record<string, Registro[]> = {};
      for (const c of def.campos.filter((x) => x.tipo === 'ref' && x.ref && pode('visualizar', x.ref.entidade))) {
        try {
          r[c.ref!.chave] = await api.get(c.ref!.rota);
        } catch {
          /* sem a lista, o campo mostra só o valor atual */
        }
      }
      if (vivo) setRefs(r);
    })();
    return () => {
      vivo = false;
    };
  }, [def, pode]);

  async function executar(acao: () => Promise<unknown>, mensagem: string) {
    setErro(null);
    setSucesso(null);
    try {
      await acao();
      setDialogo(null);
      setSucesso(mensagem);
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).message);
    }
  }

  function renderCampo(c: DefCampo, r?: Registro) {
    const bruto = r?.[c.nome];
    const valor = c.inicial && r ? c.inicial(r) : bruto === null || bruto === undefined ? '' : typeof bruto === 'boolean' ? String(bruto) : c.tipo === 'data' ? String(bruto).slice(0, 10) : String(bruto);
    return (
      <Campo key={c.nome} rotulo={c.rotulo} dica={c.ajuda ? <Ajuda titulo={c.rotulo}>{c.ajuda}</Ajuda> : undefined}>
        {(id) =>
          c.tipo === 'select' || c.tipo === 'simnao' ? (
            <select id={id} name={c.nome} required={c.obrigatorio} defaultValue={valor}>
              <option value="" disabled={c.obrigatorio}>
                {c.obrigatorio ? 'Selecione…' : '—'}
              </option>
              {(c.tipo === 'simnao' ? SIM_NAO : c.opcoes!).map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.rotulo}
                </option>
              ))}
            </select>
          ) : c.tipo === 'ref' ? (
            <select id={id} name={c.nome} required={c.obrigatorio} defaultValue={valor}>
              <option value="" disabled={c.obrigatorio}>
                {c.ref!.vazio}
              </option>
              {(refs[c.ref!.chave] ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {c.ref!.rotulo(p)}
                </option>
              ))}
              {valor && !(refs[c.ref!.chave] ?? []).some((p) => p.id === valor) && <option value={valor}>(valor atual)</option>}
            </select>
          ) : c.tipo === 'conta_pai' ? (
            <select id={id} name={c.nome} defaultValue={valor}>
              <option value="">(nenhuma — conta de nível raiz)</option>
              {contasPai
                .filter((p) => p.id !== r?.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo} — {p.nome}
                  </option>
                ))}
            </select>
          ) : c.tipo === 'area' ? (
            <textarea id={id} name={c.nome} maxLength={c.max} defaultValue={valor} rows={2} />
          ) : (
            <>
              <input
                id={id}
                name={c.nome}
                required={c.obrigatorio}
                maxLength={c.max}
                defaultValue={valor}
                type={c.tipo === 'numero' ? 'number' : c.tipo === 'data' ? 'date' : 'text'}
                inputMode={c.tipo === 'numero' ? 'numeric' : undefined}
                list={c.sugestoes ? `${id}-sug` : undefined}
              />
              {c.sugestoes && (
                <datalist id={`${id}-sug`}>
                  {c.sugestoes.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              )}
            </>
          )
        }
      </Campo>
    );
  }

  return (
    <section>
      <div className="barra-ferramentas">
        <input type="search" placeholder="Buscar" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filtrar por situação">
          <option value="">Todos</option>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
        </select>
        <span className="espaco" />
        {pers.controles}
        <span className="grupo-acao-ajuda">
          {pode('criar', def.entidade) && (
            <button className="botao-primario" onClick={() => setDialogo({ tipo: 'form' })}>
              {def.novo}
            </button>
          )}
          {def.nota && <Ajuda titulo={def.novo.replace(/^Nov[oa] /, '')}>{def.nota}</Ajuda>}
        </span>
      </div>
      {!dialogo && <Aviso>{erro}</Aviso>}
      <Aviso tipo="sucesso">{sucesso}</Aviso>
      {itens.length === 0 ? (
        <Vazio>Nenhum registro cadastrado.</Vazio>
      ) : (
        <div className="rolagem quadro-tabela">
        <table ref={pers.tabela} className="tabela">
          <thead>
            <tr>
              {def.colunas.map((c) => (
                <th key={c.rotulo}>{c.rotulo}</th>
              ))}
              <th>Status</th>
              <th className="col-acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((r) => (
              <tr key={r.id}>
                {def.colunas.map((c) => (
                  <td key={c.rotulo}>{c.valor(r)}</td>
                ))}
                <td>
                  <SeloStatus ativo={r.status === 'ativo'} />
                </td>
                <td className="col-acoes">
                  {pode('editar', def.entidade) && r.status === 'ativo' && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'form', r })}>
                      Editar
                    </button>
                  )}
                  {pode('inativar', def.entidade) && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'status', r })}>
                      {r.status === 'ativo' ? 'Inativar' : 'Reativar'}
                    </button>
                  )}
                  <a className="botao-link" href={`#/auditoria?entidade_tipo=${def.entidade}&entidade_id=${r.id}`}>
                    Histórico
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {dialogo?.tipo === 'form' && (
        <Modal titulo={dialogo.r ? `Editar ${def.singular}` : def.novo} aoFechar={() => setDialogo(null)}>
          <form
            className={`form-cadastro${def.campos.length > 6 ? ' duas-colunas' : ''}`}
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              const corpo: Record<string, unknown> = {};
              for (const c of def.campos) corpo[c.nome] = String(f.get(c.nome) ?? '') || (c.obrigatorio ? '' : null);
              if (dialogo.r) {
                corpo.atualizado_em = dialogo.r.atualizado_em;
                corpo.justificativa = f.get('justificativa') || undefined;
                executar(() => api.patch(`${def.rota}/${dialogo.r!.id}`, corpo), 'Alterações salvas.');
              } else {
                executar(() => api.post(def.rota, corpo), 'Registro criado.');
              }
            }}
          >
            <Aviso>{erro}</Aviso>
            {def.campos.map((c) => renderCampo(c, dialogo.r))}
            {dialogo.r && <Campo rotulo="Justificativa (opcional)">{(id) => <input id={id} name="justificativa" maxLength={1000} />}</Campo>}
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Salvar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'status' && (
        <ConfirmarAcao
          erro={erro}
          titulo={dialogo.r.status === 'ativo' ? `Inativar ${def.singular}` : `Reativar ${def.singular}`}
          descricao={dialogo.r.status === 'ativo' ? 'O registro deixa de estar disponível para novos lançamentos, mas o histórico é preservado (sem exclusão — DEC-10).' : 'O registro voltará a ficar disponível.'}
          rotuloConfirmar={dialogo.r.status === 'ativo' ? 'Inativar' : 'Reativar'}
          aoFechar={() => setDialogo(null)}
          aoConfirmar={(j) =>
            executar(() => api.post(`${def.rota}/${dialogo.r.id}/${dialogo.r.status === 'ativo' ? 'inativar' : 'reativar'}`, { justificativa: j || undefined }), 'Situação atualizada.')
          }
        />
      )}
    </section>
  );
}

/** parametro — versionado e imutável: nova vigência = novo registro; só é possível encerrar uma vigência aberta. */
export function TelaParametros() {
  const { pode } = useSessao();
  const [itens, setItens] = useState<Registro[]>([]);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const persParam = usePersonalizacao('parametros', { busca: { valor: busca, definir: setBusca } });
  const [dialogo, setDialogo] = useState<{ tipo: 'novo'; chave?: string } | { tipo: 'encerrar'; r: Registro } | null>(null);

  const carregar = useCallback(async () => {
    try {
      setItens(await api.get(`/api/parametros${consulta({ busca })}`));
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [busca]);
  useEffect(() => {
    carregar();
  }, [carregar]);

  async function executar(acao: () => Promise<unknown>, mensagem: string) {
    setErro(null);
    setSucesso(null);
    try {
      await acao();
      setDialogo(null);
      setSucesso(mensagem);
      await carregar();
    } catch (e) {
      setErro((e as ErroApi).message);
    }
  }

  return (
    <section>
      <Aviso tipo="info">
        Parâmetros são versionados e imutáveis: para mudar um valor, encerre a vigência atual e crie um novo registro com a nova
        data de início. O histórico nunca é sobrescrito.
      </Aviso>
      <div className="barra-ferramentas">
        <input type="search" placeholder="Buscar por chave" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar parâmetros" />
        <span className="espaco" />
        {persParam.controles}
        {pode('criar', 'parametro') && (
          <button className="botao-primario" onClick={() => setDialogo({ tipo: 'novo' })}>
            Nova vigência
          </button>
        )}
      </div>
      {!dialogo && <Aviso>{erro}</Aviso>}
      <Aviso tipo="sucesso">{sucesso}</Aviso>
      {itens.length === 0 ? (
        <Vazio>Nenhum parâmetro cadastrado.</Vazio>
      ) : (
        <div className="rolagem quadro-tabela">
        <table ref={persParam.tabela} className="tabela">
          <thead>
            <tr>
              <th>Chave</th>
              <th>Valor</th>
              <th>Vigente de</th>
              <th>Vigente até</th>
              <th>Status</th>
              <th className="col-acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((r) => (
              <tr key={r.id}>
                <td>
                  <code>{r.chave}</code>
                </td>
                <td>{r.valor}</td>
                <td>{data(r.vigente_de)}</td>
                <td>{r.vigente_ate ? data(r.vigente_ate) : 'sem fim definido'}</td>
                <td>{r.vigente_hoje ? <Selo tom="ativo">Vigente hoje</Selo> : <Selo tom="neutro">Fora de vigência</Selo>}</td>
                <td className="col-acoes">
                  {pode('editar', 'parametro') && !r.vigente_ate && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'encerrar', r })}>
                      Encerrar vigência
                    </button>
                  )}
                  {pode('criar', 'parametro') && (
                    <button className="botao-link" onClick={() => setDialogo({ tipo: 'novo', chave: r.chave })}>
                      Nova vigência
                    </button>
                  )}
                  <a className="botao-link" href={`#/auditoria?entidade_tipo=parametro&entidade_id=${r.id}`}>
                    Histórico
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {dialogo?.tipo === 'novo' && (
        <Modal titulo="Nova vigência de parâmetro" aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              executar(
                () =>
                  api.post('/api/parametros', {
                    chave: f.get('chave'),
                    valor: f.get('valor'),
                    vigente_de: f.get('vigente_de'),
                    vigente_ate: f.get('vigente_ate') || null,
                    justificativa: f.get('justificativa') || undefined,
                  }),
                'Parâmetro registrado.',
              );
            }}
          >
            <Aviso>{erro}</Aviso>
            <Campo rotulo="Chave" ajuda="Identificador do parâmetro, ex.: aliquota.iss">{(id) => <input id={id} name="chave" required maxLength={100} defaultValue={dialogo.chave ?? ''} />}</Campo>
            <Campo rotulo="Valor">{(id) => <input id={id} name="valor" required maxLength={20000} />}</Campo>
            <Campo rotulo="Vigente a partir de">{(id) => <input id={id} name="vigente_de" type="date" required />}</Campo>
            <Campo rotulo="Vigente até (opcional)">{(id) => <input id={id} name="vigente_ate" type="date" />}</Campo>
            <Campo rotulo="Justificativa (opcional)">{(id) => <input id={id} name="justificativa" maxLength={1000} />}</Campo>
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Registrar
              </button>
            </div>
          </form>
        </Modal>
      )}

      {dialogo?.tipo === 'encerrar' && (
        <Modal titulo={`Encerrar vigência — ${dialogo.r.chave}`} aoFechar={() => setDialogo(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              executar(
                () =>
                  api.post(`/api/parametros/${dialogo.r.id}/encerrar-vigencia`, {
                    vigente_ate: f.get('vigente_ate'),
                    atualizado_em: dialogo.r.atualizado_em,
                    justificativa: f.get('justificativa') || undefined,
                  }),
                'Vigência encerrada.',
              );
            }}
          >
            <Aviso>{erro}</Aviso>
            <p>
              Valor atual: <strong>{dialogo.r.valor}</strong>, vigente desde {data(dialogo.r.vigente_de)}.
            </p>
            <Campo rotulo="Último dia de vigência">{(id) => <input id={id} name="vigente_ate" type="date" required min={dialogo.r.vigente_de} />}</Campo>
            <Campo rotulo="Justificativa (registrada na auditoria)">{(id) => <input id={id} name="justificativa" maxLength={1000} />}</Campo>
            <div className="acoes-form">
              <button type="button" className="botao-secundario" onClick={() => setDialogo(null)}>
                Cancelar
              </button>
              <button className="botao-primario" type="submit">
                Encerrar vigência
              </button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}
